import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { adminSuperAdminInfoStoreQuery } from '../../graphql/queries';
import { updateStoreDeliveryAreaMutation } from '../../graphql/mutations';
import { getObjectDiff } from '@/utils/objDiff';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import i18n from '@/utils/i18n';
import { ContentSection } from '../../components/Layout/ContentSection';

type StoreDeliveryEditViewProps = {
  externalPickpackId: string | null;
  match?: { params: { storeId: string } };
  title: string;
};

export const StoreDeliveryEditView = ({
  match,
  externalPickpackId,
  title,
}: StoreDeliveryEditViewProps) => {
  const sId = match?.params.storeId || externalPickpackId;

  const [storeUpdate, setStoreUpdate] = useState<any | undefined>({});
  const [oldStoreUpdate, setOldStoreUpdate] = useState<any | undefined>({});
  const [updateStoreSuperAdmin, { loading: updating }] = useMutation(
    updateStoreDeliveryAreaMutation,
  );
  const { data = { store: null }, loading: loadingStore } = useQuery(
    adminSuperAdminInfoStoreQuery,
    {
      fetchPolicy: 'no-cache',
      skip: !sId,
      variables: { id: sId },
      onCompleted: (storeData) => {
        // @ts-ignore
        if (storeData) {
          setStoreUpdate(storeData.store);
          setOldStoreUpdate(JSON.parse(JSON.stringify(storeData.store)));
        }
      },
    },
  );

  // @ts-ignore
  function handleChange({ target: { type, name, value } }) {
    let formattedValue = value;
    if (type === 'number' || type === 'select-one' || name === 'vat_rate') {
      formattedValue = +value;
    }
    setStoreUpdate({ ...storeUpdate, [name]: formattedValue });
  }

  function handleSave() {
    const getMutationInput = (storeIn: any) => ({
      delivery_area: storeIn.delivery_area,
      sd_application_key: storeIn.sd_application_key,
      sd_store_id: storeIn.sd_store_id,
    });

    const mutationInput = getObjectDiff(
      getMutationInput(oldStoreUpdate),
      getMutationInput(storeUpdate),
    );
    console.log({ mutationInput });
    updateStoreSuperAdmin({
      variables: {
        id: sId,
        store: mutationInput,
      },
      update: (cache, { data: { updateStoreSuperAdmin: updatedStore } }) => {
        setStoreUpdate(updatedStore);
        setOldStoreUpdate(JSON.parse(JSON.stringify(updatedStore)));
      },
    }).catch((err: any) => {
      alert('Error updating store: ' + err);
    });
  }

  const handleGeoJsonChange = (e: { target: { value: string } }) => {
    const value = e.target.value;
    setStoreUpdate((oldStoreUpdate: any) => ({
      ...oldStoreUpdate,
      delivery_area: value,
    }));
  };

  if (loadingStore || !storeUpdate) {
    return <LoadingContainer />;
  }

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection
        title={`${i18n.t('Delivery:deliveryArea')} - ${storeUpdate.name}`}
        isLoading={updating || loadingStore || !storeUpdate}
        onSave={handleSave}
      >
        <EditItem title={i18n.t('Delivery:deliveryArea')}>
          <p>
            Please use{' '}
            <a
              href="http://geojson.io/"
              target="_blank"
              rel="noopener noreferrer"
            >
              geojson.io
            </a>{' '}
            to draw the delivery area and paste the GeoJSON here:
          </p>
          <div className="input-group mb-3">
            <textarea
              rows={10}
              className="form-control"
              name="delivery_area_geojson"
              value={storeUpdate.delivery_area}
              onChange={handleGeoJsonChange}
            />
          </div>
        </EditItem>
        <EditItem title={i18n.t('Delivery:simplyDeliveryIntegration')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Delivery:applicationKey')}
            </span>
            <input
              type="text"
              className="form-control"
              value={storeUpdate.sd_application_key || ''}
              name="sd_application_key"
              onChange={handleChange}
              autoComplete="off"
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:storeId')}
            </span>
            <input
              type="text"
              className="form-control"
              value={storeUpdate.sd_store_id || ''}
              name="sd_store_id"
              onChange={handleChange}
              autoComplete="off"
            />
          </div>
        </EditItem>
      </EditSection>
    </ContentSection>
  );
};
